import { useTranslation } from 'react-i18next';
import { CircleAlert } from 'lucide-react';
import type { DraftBlocker } from '@/lib/draft-order-client';

interface CartBlockersProps {
  blockers: DraftBlocker[];
  currencySymbol: string;
}

/**
 * Why Confirm is disabled, in the operator's words.
 *
 * These are the very same checks the agent runs before placing an order — rendering them as a list
 * rather than a single "cannot confirm" is what makes the panel actionable: each line names one
 * thing to fix.
 */
export function CartBlockers({ blockers, currencySymbol }: CartBlockersProps) {
  const { t } = useTranslation('conversations');
  if (blockers.length === 0) return null;

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800/60 dark:bg-amber-950/40">
      <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-amber-800 dark:text-amber-300">
        <CircleAlert className="h-3.5 w-3.5 shrink-0" />
        {t('cart.blockers.title')}
      </p>
      <ul className="ml-5 list-disc space-y-0.5 text-xs text-amber-800 dark:text-amber-300">
        {blockers.map((blocker) => (
          <li key={blocker.code}>{describeBlocker(blocker, currencySymbol, t)}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * One blocker as a sentence, with the numbers and branch names filled in.
 *
 * Exported because a refused *confirmation* comes back as a 422 carrying the first blocker's code and
 * data, and the toast that reports it should say exactly what this list says. `code` is widened to
 * `string` for that caller — an HTTP error code is not typed as a blocker — and anything unrecognized
 * falls back to `fallback`.
 */
export function describeBlocker(
  blocker: { code: string; data?: Record<string, unknown> },
  currencySymbol: string,
  t: ReturnType<typeof useTranslation<'conversations'>>['t'],
  fallback?: string,
): string {
  const data = blocker.data ?? {};

  if (blocker.code === 'BELOW_MINIMUM') {
    const symbol = typeof data.currencySymbol === 'string' ? data.currencySymbol : currencySymbol;
    return t('cart.blockers.BELOW_MINIMUM', {
      minimum: `${symbol}${Number(data.minimumAmount ?? 0).toFixed(2)}`,
      total: `${symbol}${Number(data.total ?? 0).toFixed(2)}`,
    });
  }

  if (blocker.code === 'DELIVERY_FEE_CHANGED') {
    return t('cart.blockers.DELIVERY_FEE_CHANGED', {
      previous: `${currencySymbol}${Number(data.previousFee ?? 0).toFixed(2)}`,
      fee: `${currencySymbol}${Number(data.deliveryFee ?? 0).toFixed(2)}`,
    });
  }

  if (blocker.code === 'COUPON_MINIMUM_NOT_MET') {
    const symbol = typeof data.currencySymbol === 'string' ? data.currencySymbol : currencySymbol;
    return t('cart.blockers.COUPON_MINIMUM_NOT_MET', {
      code: String(data.code ?? ''),
      minimum: `${symbol}${Number(data.minimumOrderAmount ?? 0).toFixed(2)}`,
      shortfall: `${symbol}${Number(data.shortfall ?? 0).toFixed(2)}`,
    });
  }

  // The `reason` these carry (EXPIRED / DISABLED / LIMIT_REACHED …) is deliberately not spelled out:
  // the operator's next move is the same either way — drop the coupon or leave it — and one sentence
  // per blocker is what keeps this list scannable.
  if (
    blocker.code === 'COUPON_INVALID' ||
    blocker.code === 'COUPON_LIMIT_REACHED' ||
    blocker.code === 'COUPON_SCHEDULE_NOT_ACTIVE'
  ) {
    return t(`cart.blockers.${blocker.code}`, { code: String(data.code ?? '') });
  }

  if (
    blocker.code === 'LOCATION_CLOSED' ||
    blocker.code === 'DELIVERY_NOT_AVAILABLE' ||
    blocker.code === 'DELIVERY_CLOSED' ||
    blocker.code === 'PICKUP_NOT_AVAILABLE'
  ) {
    return t(`cart.blockers.${blocker.code}`, { name: String(data.locationName ?? '') });
  }

  return t(`cart.blockers.${blocker.code}` as `cart.blockers.${DraftBlocker['code']}`, {
    defaultValue: fallback ?? blocker.code,
  });
}
